import { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { CloseOutlined } from "@mui/icons-material";

import { RequestNextApi } from "../../api/Request";
import { ProgressContext } from "../Context/progressContext";
import { getCookie } from "../../utils/cookieHandle";
import { ShowNotification } from "../../utils/notification/snackbarUtils";

export default function Preferences({ userId }) {
    const { update: updateProgress } = useContext(ProgressContext);
    const [prefChecked, setPrefChecked] = useState({
        commNoNewsletterMail: false,
        commNoStudyGroupMail: false,
        commNoRenewMail: false,
        commNoPartnerMail: false,
        commNoMail: false,
        commNoNewsletterEmail: false,
        commGlobalUnsubscribe: false,
        commNoStudyGroupEmail: false,
        commNoRenewEmail: false,
        commNoPartnerEmail: false,
        commNoEmail: false,
        commNoConferenceEmail: false,
    });
    const [mailActivity, setMailActivity] = useState([]);
    const [loadingMailActivity, setLoadingMailActivity] = useState(true);
    const [mailEvent, setMailEvent] = useState();
    const [mailStatus, setMailStatus] = useState();
    const [sendGridKey, setSendGridKey] = useState();
    const [email, setEmail] = useState();
    const requestBody = {
        jwtToken: getCookie("jwt-token"),
        ...(userId ? { userId } : {}),
    };

    const fetchData = async () => {
        updateProgress({ isLoading: true });

        try {
            const result = await RequestNextApi.post(
                "/user/user-fetch2",
                requestBody
            );
            if (result.data.success) {
                setPrefChecked({
                    commNoNewsletterMail: Boolean(!result.data.user.commNoNewsletterMail),
                    commNoStudyGroupMail: Boolean(!result.data.user.commNoStudyGroupMail),
                    commNoRenewMail: Boolean(!result.data.user.commNoRenewMail),
                    commNoPartnerMail: Boolean(!result.data.user.commNoPartnerMail),
                    commNoMail: Boolean(!result.data.user.commNoMail),
                    commNoNewsletterEmail: Boolean(
                        !result.data.user.commNoNewsletterEmail
                    ),
                    commGlobalUnsubscribe: Boolean(
                        result.data.user.commGlobalUnsubscribe
                    ),
                    commNoStudyGroupEmail: Boolean(
                        !result.data.user.commNoStudyGroupEmail
                    ),
                    commNoRenewEmail: Boolean(!result.data.user.commNoRenewEmail),
                    commNoPartnerEmail: Boolean(!result.data.user.commNoPartnerEmail),
                    commNoEmail: Boolean(!result.data.user.commNoEmail),
                    commNoConferenceEmail: Boolean(
                        !result.data.user.commNoConferenceEmail
                    ),
                });
                setEmail(result.data.user.email);
            } else {
                ShowNotification.error(result.data.message);
            }
        } catch (error) {
            ShowNotification.error("Fetching settings info failed.");
        }

        await fetchMailStatus();

        updateProgress({ isLoading: false });
    };

    const fetchMailStatus = async () => {
        try {
            const result = await RequestNextApi.post(
                "/user/check-email-blocked",
                requestBody
            );
            if (result.data.success) {
                const reason =
                    result.data.block?.reason ||
                    result.data.spam?.reason ||
                    result.data.bounce?.reason ||
                    result.data.invalid?.reason ||
                    result.data.globalBlocked?.reason ||
                    "";
                setMailStatus(
                    reason
                        ? `Kingdom Advisors cannot successfully deliver mail to you. ${reason}`
                        : ""
                );
            } else {
                ShowNotification.error(result.data.message);
            }
        } catch (error) {
            ShowNotification.error("Fetching email status failed.");
        }
    };

    const fetchMailHistory = async () => {
        try {
            const result = await RequestNextApi.post(
                "/user/user-mail-activity",
                requestBody
            );
            if (result.data.success) {
                setMailActivity(result.data.mailActivity);
            } else {
                ShowNotification.error(result.data.message);
            }
        } catch (error) {
            ShowNotification.error("Fetching mail activity failed.");
        }
        setLoadingMailActivity(false);
    };

    const fetchSendGridKey = async (_email) => {
        try {
            const result = await RequestNextApi.post("/user/get-sendgrid-key", {
                email: _email,
            });
            setSendGridKey(result.data.key);
        } catch (error) {
            ShowNotification.error("Fetching sendgrid key failed.");
        }
    };

    useEffect(() => {
        fetchData();
        fetchMailHistory();
    }, []);

    useEffect(() => {
        if (email) {
            fetchSendGridKey(email);
        }
    }, [email]);

    const handleClickPref = (e) => {
        if (e.target.name === "commGlobalUnsubscribe") {
            setPrefChecked((prevState) => {
                let newState = { ...prevState };
                Object.keys(newState).forEach((key) => {
                    newState[key] = false;
                });
                newState[e.target.name] = true;
                return newState;
            });
        } else {
            setPrefChecked({
                ...prefChecked,
                ["commGlobalUnsubscribe"]: false,
                [e.target.name]: e.target.checked,
            });
        }
    };

    const handleClickSave = async () => {
        updateProgress({ isLoading: true });
        try {
            const result = await RequestNextApi.post("/user/user-save", {
                ...requestBody,
                commNoNewsletterMail: Boolean(!prefChecked.commNoNewsletterMail),
                commNoStudyGroupMail: Boolean(!prefChecked.commNoStudyGroupMail),
                commNoRenewMail: Boolean(!prefChecked.commNoRenewMail),
                commNoPartnerMail: Boolean(!prefChecked.commNoPartnerMail),
                commNoMail: Boolean(!prefChecked.commNoMail),
                commNoNewsletterEmail: Boolean(!prefChecked.commNoNewsletterEmail),
                commGlobalUnsubscribe: Boolean(prefChecked.commGlobalUnsubscribe),
                commNoStudyGroupEmail: Boolean(!prefChecked.commNoStudyGroupEmail),
                commNoRenewEmail: Boolean(!prefChecked.commNoRenewEmail),
                commNoPartnerEmail: Boolean(!prefChecked.commNoPartnerEmail),
                commNoEmail: Boolean(!prefChecked.commNoEmail),
                commNoConferenceEmail: Boolean(!prefChecked.commNoConferenceEmail),
            });
            if (result.data.success) {
                await fetchMailStatus();

                ShowNotification.success(result.data.message);
            } else {
                ShowNotification.error(result.data.message);
            }
        } catch (error) {
            ShowNotification.error("Failed to save information.");
        }
        updateProgress({ isLoading: false });
    };

    const handleRemoval = async () => {
        updateProgress({ isLoading: true });

        try {
            const result = await RequestNextApi.post(
                "/user/remove-email-blocked",
                requestBody
            );
            if (result.data.success) {
                setPrefChecked({
                    commNoNewsletterMail: Boolean(!result.data.user.commNoNewsletterMail),
                    commNoStudyGroupMail: Boolean(!result.data.user.commNoStudyGroupMail),
                    commNoRenewMail: Boolean(!result.data.user.commNoRenewMail),
                    commNoPartnerMail: Boolean(!result.data.user.commNoPartnerMail),
                    commNoMail: Boolean(!result.data.user.commNoMail),
                    commNoNewsletterEmail: Boolean(
                        !result.data.user.commNoNewsletterEmail
                    ),
                    commGlobalUnsubscribe: Boolean(
                        result.data.user.commGlobalUnsubscribe
                    ),
                    commNoStudyGroupEmail: Boolean(
                        !result.data.user.commNoStudyGroupEmail
                    ),
                    commNoRenewEmail: Boolean(!result.data.user.commNoRenewEmail),
                    commNoPartnerEmail: Boolean(!result.data.user.commNoPartnerEmail),
                    commNoEmail: Boolean(!result.data.user.commNoEmail),
                    commNoConferenceEmail: Boolean(!result.data.user.commNoConferenceEmail),
                });
            } else {
                ShowNotification.error(result.data.message);
            }
        } catch (error) {
            ShowNotification.error("Remove email request failed.");
        }
        await fetchMailStatus();
        updateProgress({ isLoading: false });
    };

    const handleShowHistory = async (messageId) => {
        updateProgress({ isLoading: true });

        try {
            const result = await RequestNextApi.post("/user/sendgrid-mail-activity", {
                ...requestBody,
                messageId,
            });
            if (result.data.success) {
                setMailEvent(result.data.event);
            } else {
                ShowNotification.error(result.data.message);
            }
        } catch (error) {
            ShowNotification.error("Fetch mail activity failed.");
        }

        updateProgress({ isLoading: false });
    };

    return (
        <div>
            <div className="gap-5 flex flex-col mt-6">
                <div className="flex flex-col lg:flex-row border px-5 py-6 rounded-xl border-solid border-primary-100 shadow gap-x-10 gap-y-6">
                    <div className="bg-white flex flex-col lg:flex-row gap-x-8 gap-y-6">
                        <div className="w-full lg:w-[280px]">
                            <Typography variant="h4" color="secondary">
                                Email Status
                            </Typography>
                        </div>
                        <div className={`flex flex-col gap-y-2 flex-1`}>
                            <Box>
                                <Typography
                                    variant="body1"
                                    color={mailStatus ? "error" : "inherit"}
                                >
                                    {mailStatus || "OK"}
                                </Typography>
                                {mailStatus && (
                                    <Button
                                        sx={{ mt: 2 }}
                                        color="secondary"
                                        variant="contained"
                                        className="normal-case"
                                        onClick={handleRemoval}
                                    >
                                        Request removal from this blocked group
                                    </Button>
                                )}
                            </Box>
                        </div>
                    </div>
                    {sendGridKey && !!userId && (
                        <div className="bg-white flex flex-col lg:flex-row gap-x-8 gap-y-6">
                            <div className="">
                                <Typography variant="h4" color="secondary">
                                    SendGrid Key
                                </Typography>
                            </div>
                            <div className={`flex flex-col gap-y-2 flex-1`}>
                                <Box>
                                    <Typography
                                        variant="body1"
                                        color={mailStatus ? "error" : "inherit"}
                                    >
                                        {sendGridKey}
                                    </Typography>
                                </Box>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow flex flex-col lg:flex-row gap-x-8 gap-y-6">
                    <div className="w-full lg:w-[280px]">
                        <Typography variant="h4" color="secondary">
                            Email Opt IN Preferences
                        </Typography>
                    </div>
                    <div className={`flex flex-col gap-y-2 flex-1`}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={prefChecked.commNoConferenceEmail}
                                    name="commNoConferenceEmail"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Subscribe to Information about the Annual Kingdom Advisors Conference"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={prefChecked.commNoNewsletterEmail}
                                    name="commNoNewsletterEmail"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Subscribe to Monthly Newsletter"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={prefChecked.commNoStudyGroupEmail}
                                    name="commNoStudyGroupEmail"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Subscribe to Communications from my Study Group"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={prefChecked.commNoEmail}
                                    name="commNoEmail"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Subscribe to Information on New Products, Tools or Events"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={prefChecked.commNoPartnerEmail}
                                    name="commNoPartnerEmail"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Subscribe to Information and Resource Offerings from Partners of Kingdom Advisors"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={prefChecked.commNoRenewEmail}
                                    name="commNoRenewEmail"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Allow Administrative Emails"
                        />
                        <FormControlLabel
                            sx={{ mt: 5 }}
                            control={
                                <Checkbox
                                    checked={prefChecked.commGlobalUnsubscribe}
                                    name="commGlobalUnsubscribe"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Unsubscribe from ALL email"
                        />
                        <Box>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleClickSave}
                            >
                                Save
                            </Button>
                        </Box>
                    </div>
                </div>

                <div className="bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow flex flex-col lg:flex-row gap-x-8 gap-y-6">
                    <div className="w-full lg:w-[280px]">
                        <Typography variant="h4" color="secondary">
                            30 Day Email History
                        </Typography>
                    </div>
                    <div className={`flex flex-col gap-y-2 flex-1`}>
                        {loadingMailActivity && (
                            <Typography variant="body1">Loading...</Typography>
                        )}
                        {mailActivity.length > 0 && (
                            <TableContainer sx={{ maxHeight: 290 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Message</TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {mailActivity.map((activity) => (
                                            <TableRow key={activity.msg_id}>
                                                <TableCell className="capitalize">
                                                    {activity.status}
                                                </TableCell>
                                                <TableCell>{activity.subject}</TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={() => handleShowHistory(activity.msg_id)}
                                                    >
                                                        More
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </div>
                </div>

                <div className="bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow flex flex-col lg:flex-row gap-x-8 gap-y-6">
                    <div className="w-full lg:w-[280px]">
                        <Typography variant="h4" color="secondary">
                            Physical Mail Opt IN Preferences
                        </Typography>
                    </div>
                    <div className={`flex flex-col gap-y-2 flex-1`}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={prefChecked.commNoNewsletterMail}
                                    name="commNoNewsletterMail"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Monthly Newsletter"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={prefChecked.commNoStudyGroupMail}
                                    name="commNoStudyGroupMail"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Communications from my Study Group"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={prefChecked.commNoMail}
                                    name="commNoMail"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Information on New Products, Tools or Events"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={prefChecked.commNoPartnerMail}
                                    name="commNoPartnerMail"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Information and Resource Offerings from Partners of Kingdom Advisors"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={prefChecked.commNoRenewMail}
                                    name="commNoRenewMail"
                                    color="secondary"
                                    onChange={handleClickPref}
                                />
                            }
                            label="Administrative Emails"
                        />
                        <Box>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleClickSave}
                            >
                                Save
                            </Button>
                        </Box>
                    </div>
                </div>
            </div>
            <Modal onClose={() => setMailEvent()} open={!!mailEvent}>
                <div className="border border-solid border-primary-100 shadow relative max-w-full w-[700px] bg-white mx-auto p-5 rounded-xl top-1/2 -translate-y-1/2 max-h-screen overflow-scroll space-y-4">
                    {mailEvent && (
                        <Box>
                            <div className="flex gap-4 items-center relative">
                                <div>
                                    <Typography variant="h4" className="text-primary-900 mb-2">
                                        Subject: {mailEvent.subject}
                                    </Typography>
                                    <Typography variant="body1" color="secondary">
                                        From: {mailEvent.from_email}
                                    </Typography>
                                </div>

                                <div className="absolute top-0 right-0">
                                    <IconButton color="primary" onClick={() => setMailEvent()}>
                                        <CloseOutlined />
                                    </IconButton>
                                </div>
                            </div>

                            <TableContainer sx={{ maxHeight: 290, mt: 2 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Event Name</TableCell>
                                            <TableCell>Date/Time</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {mailEvent.events.map((event, index) => (
                                            <TableRow key={index}>
                                                <TableCell className="capitalize">
                                                    {event.event_name}
                                                </TableCell>
                                                <TableCell>
                                                    {dayjs.utc(event?.processed_datetime?.date).local().format(
                                                        "M/D/YYYY h:mm A"
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </div>
            </Modal>
        </div>
    );
}
